import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { colors } from '../theme';
import { modalStyles } from './modalStyles';
import { formatDDMMYYYY } from '../utils/dates';
import SubmissionImageGallery from './SubmissionImageGallery';
import RmaCaseImages from './RmaCaseImages';
import RmaCustomerSummary from './RmaCustomerSummary';
import CloseCaseAction from './CloseCaseAction';
import fieldConstants from '../constants/rmaFieldConstants.json';
import { deviceList, repairProgress, allDevicesResolved, REPAIR_STATE_LABELS } from '../utils/rmaDevices';

const FAULT_CATEGORIES = fieldConstants.repairFaultCategories || [];

// Per-unit rows for a bundled case (issue #712).
const deviceRowStyles = {
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '0.55rem 0.7rem',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 8,
    marginBottom: '0.45rem',
    background: 'rgba(255,255,255,0.03)',
  },
  primaryTag: {
    marginLeft: '0.4rem',
    fontSize: '0.62rem',
    fontWeight: 700,
    color: '#46bb89',
    background: 'rgba(70,187,137,0.14)',
    border: '1px solid rgba(70,187,137,0.35)',
    borderRadius: 999,
    padding: '0.05rem 0.35rem',
  },
};

const API_BASE = process.env.REACT_APP_API_URL;

const badgeStyles = {
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginBottom: '0.75rem'
  },
  badge: {
    padding: '0.25rem 0.6rem',
    borderRadius: '999px',
    backgroundColor: 'rgba(255,255,255,0.1)',
    color: colors.textStrong,
    fontSize: '0.8rem',
    fontWeight: 600,
    textTransform: 'capitalize'
  }
};

const sectionStyles = {
  container: {
    margin: '1rem 0'
  },
  heading: {
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: '0.5rem',
    color: colors.textStrong
  },
  subtitle: {
    marginBottom: '0.75rem',
    color: colors.textMuted,
    fontSize: '0.9rem'
  }
};

const inputBase = {
  width: '100%',
  padding: '0.4rem 0.6rem',
  borderRadius: '6px',
  border: `1px solid ${colors.panelBorder}`,
  backgroundColor: 'rgba(255,255,255,0.05)',
  color: colors.textStrong,
  fontSize: '0.9rem'
};

const checklistStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem'
  },
  taskRow: {
    border: `1px solid ${colors.panelBorder}`,
    borderRadius: '10px',
    padding: '0.85rem',
    backgroundColor: 'rgba(255,255,255,0.03)',
    display: 'grid',
    gap: '0.5rem'
  },
  labelInput: {
    ...inputBase
  },
  noteInput: {
    ...inputBase,
    minHeight: '60px',
    resize: 'vertical'
  },
  controls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontWeight: 500,
    color: colors.textBody
  },
  removeButton: {
    background: 'transparent',
    border: 'none',
    color: colors.danger,
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 600
  }
};

const warrantyStyles = {
  options: {
    display: 'flex',
    gap: '0.75rem',
    marginBottom: '0.75rem'
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.45rem 0.75rem',
    borderRadius: '999px',
    border: `1px solid ${colors.panelBorder}`,
    cursor: 'pointer',
    color: colors.textBody,
    backgroundColor: 'rgba(255,255,255,0.04)'
  },
  optionActive: {
    backgroundColor: colors.primary,
    color: '#11201c',
    borderColor: colors.primary
  },
  note: {
    ...inputBase,
    minHeight: '70px',
    marginBottom: '0.5rem',
    resize: 'vertical'
  },
  lockNote: {
    fontSize: '0.85rem',
    color: colors.success,
    marginTop: '0.25rem'
  }
};

const summaryStyles = {
  textarea: {
    ...inputBase,
    minHeight: '90px',
    resize: 'vertical'
  }
};

const challengeStyles = {
  container: {
    border: `1px solid ${colors.panelBorder}`,
    borderRadius: '10px',
    padding: '1rem',
    backgroundColor: 'rgba(255,255,255,0.03)',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem'
  },
  headerRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem'
  },
  meta: {
    fontSize: '0.85rem',
    color: colors.textMuted
  },
  message: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: '8px',
    padding: '0.75rem',
    whiteSpace: 'pre-wrap',
    lineHeight: 1.5,
    color: colors.textBody
  },
  attachments: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem'
  },
  attachmentLink: {
    color: colors.accent,
    textDecoration: 'underline',
    fontSize: '0.9rem'
  },
  decisionRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.65rem',
    marginTop: '0.5rem'
  },
  radioGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem'
  },
  radioOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.4rem 0.75rem',
    borderRadius: '999px',
    border: `1px solid ${colors.panelBorder}`,
    cursor: 'pointer',
    backgroundColor: 'rgba(255,255,255,0.04)',
    color: colors.textBody
  },
  radioActive: {
    borderColor: colors.accent,
    color: colors.accent,
    backgroundColor: 'rgba(70,187,137,0.1)'
  },
  noteInput: {
    ...inputBase,
    minHeight: '70px',
    resize: 'vertical'
  },
  statusPillAccepted: {
    backgroundColor: colors.success,
    color: '#11201c',
    padding: '0.3rem 0.85rem',
    borderRadius: '999px',
    fontSize: '0.85rem',
    fontWeight: 600
  },
  statusPillDeclined: {
    backgroundColor: colors.statusYellow,
    color: '#2b2310',
    padding: '0.3rem 0.85rem',
    borderRadius: '999px',
    fontSize: '0.85rem',
    fontWeight: 600
  },
  historyContainer: {
    marginTop: '0.5rem'
  },
  historySummary: {
    cursor: 'pointer',
    fontWeight: 600,
    color: colors.textMuted
  },
  historyList: {
    marginTop: '0.75rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem'
  },
  historyItem: {
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderRadius: '8px',
    padding: '0.6rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem'
  },
  historyHeader: {
    fontWeight: 600,
    color: colors.textStrong
  },
  historyMeta: {
    fontSize: '0.85rem',
    color: colors.textMuted
  },
  historyNote: {
    fontSize: '0.9rem',
    color: colors.textBody
  },
  feedback: {
    fontSize: '0.85rem',
    color: colors.success
  },
  error: {
    fontSize: '0.85rem',
    color: colors.statusYellow
  }
};

const formatDateTime = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleString('no-NO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const inspectionFormStyles = {
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
    marginBottom: '0.75rem'
  },
  label: {
    fontWeight: 600,
    color: colors.textStrong
  },
  input: {
    ...inputBase
  },
  inline: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.65rem',
    marginBottom: '0.75rem'
  },
  hint: {
    fontSize: '0.85rem',
    color: colors.textMuted
  }
};

const CUSTOM_CATEGORIES_KEY = 'ni_repair_task_categories';

const PREDEFINED_CATEGORIES = [
  'Replace component',
  'Board replacement',
  'Firmware update',
  'Software fix',
  'Diagnostics',
  'Hardware inspection',
  'Cable repair',
  'Clean',
  'Calibration',
  'Testing',
];

function loadCustomCategories() {
  try {
    const raw = localStorage.getItem(CUSTOM_CATEGORIES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(s => typeof s === 'string' && s.trim()) : [];
  } catch (_) {
    return [];
  }
}

function saveCustomCategories(cats) {
  try {
    localStorage.setItem(CUSTOM_CATEGORIES_KEY, JSON.stringify(cats));
  } catch (_) {}
}

const defaultTask = () => ({ label: '', done: false, note: '', dueDate: '', completedAt: null });

function normalizeTasks(rawTasks) {
  if (!Array.isArray(rawTasks) || !rawTasks.length) {
    return [];
  }
  return rawTasks.map(task => ({
    label: typeof task?.label === 'string' ? task.label : '',
    done: task?.done === true,
    note: typeof task?.note === 'string' ? task.note : '',
    dueDate: task?.dueDate
      ? (typeof task.dueDate === 'string' ? task.dueDate : new Date(task.dueDate).toISOString()).slice(0, 10)
      : '',
    completedAt: task?.completedAt || null
  }));
}

function deriveIssueBadges(rma) {
  const badges = new Set();
  if (rma.issueType) {
    badges.add(rma.issueType.replace(/_/g, ' '));
  }
  if (rma.returnReason) {
    badges.add(rma.returnReason.replace(/_/g, ' '));
  }
  if (Array.isArray(rma.repairCategories)) {
    rma.repairCategories
      .filter(Boolean)
      .forEach(value => badges.add(value.toString().replace(/_/g, ' ')));
  }
  return Array.from(badges);
}

function sanitizeTasksForSubmit(tasks) {
  return tasks
    .map(task => ({
      label: task.label.trim(),
      done: task.done === true,
      note: task.note.trim(),
      dueDate: task.dueDate || null,
      completedAt: task.completedAt || null
    }))
    .filter(task => task.label || task.note);
}

export default function RmaModalRepairing({ rma, onClose, onSave }) {
  const { getToken } = useAuth();
  const [tasks, setTasks] = useState(() => {
    const normalized = normalizeTasks(rma.repair?.tasks);
    return normalized.length ? normalized : [defaultTask()];
  });
  const [customCategories, setCustomCategories] = useState(() => loadCustomCategories());
  const [taskOtherInputs, setTaskOtherInputs] = useState({}); // index → draft string
  const [summary, setSummary] = useState(rma.repair?.summary || '');
  const [warrantyDecision, setWarrantyDecision] = useState(
    rma.repair?.warranty?.decision || null
  );
  const [warrantyNote, setWarrantyNote] = useState(rma.repair?.warranty?.note || '');
  const [warrantyLocked, setWarrantyLocked] = useState(
    Boolean(rma.repair?.warranty?.decision)
  );
  const [showWarrantyConfirm, setShowWarrantyConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');
  // Which unit of a bundled case is currently being saved (issue #712).
  const [deviceSaving, setDeviceSaving] = useState('');
  const [quoteAmount, setQuoteAmount] = useState(
    typeof rma.repair?.quote?.amount === 'number'
      ? String(rma.repair.quote.amount)
      : ''
  );
  const [repairDays, setRepairDays] = useState(
    typeof rma.repair?.timing?.repairDays === 'number'
      ? String(rma.repair.timing.repairDays)
      : ''
  );
  const [partsInStock, setPartsInStock] = useState(
    rma.repair?.timing?.parts?.inStock === false ? false : true
  );
  const [partsEtaDate, setPartsEtaDate] = useState(() => {
    const eta = rma.repair?.timing?.parts?.etaDate;
    if (!eta) return '';
    const parsed = typeof eta === 'string' ? eta : eta?.toISOString?.();
    if (!parsed) return '';
    return parsed.slice(0, 10);
  });
  const [expectedCompletion, setExpectedCompletion] = useState(
    rma.repair?.timing?.expectedCompletion || ''
  );
  const [firmwareVersion, setFirmwareVersion] = useState(rma.firmwareVersion || '');
  const [assessmentNotes, setAssessmentNotes] = useState(rma.assessmentNotes || '');
  const [environmentCode, setEnvironmentCode] = useState(rma.environmentCode || '');
  const [failureMode, setFailureMode] = useState(rma.outcome?.failureMode || '');
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [faultCategories, setFaultCategories] = useState(() => {
    const existing = Array.isArray(rma.repairCategories) ? rma.repairCategories : [];
    return existing.filter(v => FAULT_CATEGORIES.some(f => f.value === v));
  });
  const [inspectionError, setInspectionError] = useState('');
  const [challengeDecision, setChallengeDecision] = useState('');
  const [challengeStaffNote, setChallengeStaffNote] = useState('');
  const [challengeError, setChallengeError] = useState('');
  const [challengeFeedback, setChallengeFeedback] = useState('');
  const [challengeSaving, setChallengeSaving] = useState(false);
  const [tasksDirty, setTasksDirty] = useState(false);
  const [tasksSaving, setTasksSaving] = useState(false);
  const [tasksSavedFeedback, setTasksSavedFeedback] = useState('');
  const [paymentSaving, setPaymentSaving] = useState(false);

  const isAwaitingRepair = rma.status === 'awaiting_repair';
  const issueBadges = useMemo(() => deriveIssueBadges(rma), [rma]);
  const quoteCurrency = 'EUR';
  // Derived, not just read. Cases finalized before the phase regression was
  // fixed are stored as 'inspection' while the customer has already accepted --
  // a state that cannot progress, because the Payment section only exists in
  // execution. Reading the facts rather than the stored field heals those on
  // sight, with no migration (issue #757).
  const approvalAlreadyAccepted = rma.repair?.customerApproval?.status === 'accepted';
  const repairPhase =
    rma.repair?.phase === 'execution' || (approvalAlreadyAccepted && rma.repair?.inspectionFinalizedAt)
      ? 'execution'
      : 'inspection';
  const isInspectionPhase = repairPhase === 'inspection';
  const warrantyDecisionValue = rma.repair?.warranty?.decision || null;
  const customerApprovalRequired = rma.repair?.customerApproval?.required === true;
  const customerApprovalStatus = rma.repair?.customerApproval?.status || 'pending';
  const waitingForCustomer = customerApprovalRequired && customerApprovalStatus === 'pending';
  const customerApproved = customerApprovalStatus === 'accepted';
  const inspectionFinalizedAt = rma.repair?.inspectionFinalizedAt || null;
  const inspectionFinalizedBy = rma.repair?.inspectionFinalizedBy || '';
  // A denied warranty keeps the phase at 'inspection' after finalizing, because
  // the case really is still in inspection -- it is waiting for the customer to
  // accept the quote. But the work is done, so the form must stop offering to
  // do it again (issue #757).
  const inspectionAwaitingCustomer =
    isInspectionPhase && Boolean(inspectionFinalizedAt) && waitingForCustomer;
  const stageLabel = isInspectionPhase ? 'Stage 1/2 – Inspection' : 'Stage 2/2 – Execution';
  const warrantyLabel = warrantyDecisionValue
    ? `Warranty ${warrantyDecisionValue}`
    : 'Warranty pending';
  const timingParts = rma.repair?.timing?.parts || {};
  const waitDays = typeof timingParts.waitDays === 'number' ? timingParts.waitDays : null;
  const expectedCompletionDisplay = expectedCompletion || rma.repair?.timing?.expectedCompletion || '';

  // Live-computed values for inspection form preview (before submission)
  const formWaitDays = useMemo(() => {
    if (partsInStock || !partsEtaDate) return null;
    const [y, m, d] = partsEtaDate.split('-').map(Number);
    if (!y || !m || !d) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const eta = new Date(y, m - 1, d); // local midnight — avoids UTC offset shift
    return Math.max(0, Math.round((eta - today) / (1000 * 60 * 60 * 24)));
  }, [partsInStock, partsEtaDate]);

  const formExpectedCompletion = useMemo(() => {
    const days = Number(repairDays);
    if (!Number.isFinite(days) || days <= 0) return '';
    let base;
    if (!partsInStock && partsEtaDate) {
      const [y, m, d] = partsEtaDate.split('-').map(Number);
      if (!y || !m || !d) return '';
      base = new Date(y, m - 1, d);
    } else {
      base = new Date(); base.setHours(0, 0, 0, 0);
    }
    base.setDate(base.getDate() + days);
    return formatDDMMYYYY(base);
  }, [repairDays, partsInStock, partsEtaDate]);
  const etaDisplay = formatDDMMYYYY(partsEtaDate || timingParts.etaDate || '') || '';
  const paymentData = rma.repair?.payment && typeof rma.repair.payment === 'object' ? rma.repair.payment : {};
  const paymentRequired = paymentData.required === true;
  const paymentReceived = paymentData.status === 'received';
  const paymentReceivedAt = paymentData.receivedAt ? new Date(paymentData.receivedAt).toLocaleString('no-NO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null;
  // A bundled case ships as one: every unit must be finished first (issue #712).
  const caseDevices = deviceList(rma);
  const deviceProgress = repairProgress(rma);
  const devicesResolved = allDevicesResolved(rma);
  const isBundle = Array.isArray(rma.devices) && rma.devices.length > 1;
  // Finalize Repair is what moves the case to awaiting_shipping. Three separate
  // things can hold it, and their status used to be scattered across the modal --
  // approval as a badge at the top, payment in its own section, units near the
  // bottom -- with a dead button at the end saying none of it. Naming them at the
  // point of the decision is the difference between "the button is broken" and
  // "I know what is left to do" (issue #757).
  const formattedPaymentAmount = (() => {
    const value = Number(paymentData.amount);
    if (!Number.isFinite(value)) return '';
    try {
      return new Intl.NumberFormat('no-NO', { style: 'currency', currency: paymentData.currency || 'EUR' }).format(value);
    } catch (err) {
      return `${paymentData.currency || 'EUR'} ${value.toFixed(2)}`;
    }
  })();

  const shipBlockers = [];
  if (customerApprovalRequired && customerApprovalStatus !== 'accepted') {
    shipBlockers.push({
      key: 'approval',
      text: customerApprovalStatus === 'declined'
        ? 'The customer declined the repair — close the case instead of shipping it.'
        : 'Waiting for the customer to accept the quote. Nothing to do here until they answer.',
    });
  }
  if (paymentRequired && !paymentReceived) {
    // The only blocker service can clear from this screen, so it carries the
    // action. Naming a problem at the bottom of the modal while its button sits
    // at the top is half a message (issue #757).
    shipBlockers.push({
      key: 'payment',
      text: paymentData.amount != null
        ? `Payment of ${formattedPaymentAmount} has not been confirmed.`
        : 'Payment has not been confirmed.',
      actionLabel: paymentSaving ? 'Confirming…' : 'Confirm payment received',
      // Called lazily: handleConfirmPayment is declared further down the
      // component, so naming it here directly would read it before init.
      onAction: () => handleConfirmPayment(),
      actionDisabled: paymentSaving,
    });
  }
  if (!devicesResolved) {
    shipBlockers.push({
      key: 'units',
      text: `${deviceProgress.done} of ${deviceProgress.total} units are finished — mark the rest done under Units on this case.`,
    });
  }
  const canFinalizeRepair = shipBlockers.length === 0;
  const sortedChallenges = useMemo(() => {
    const entries = Array.isArray(rma.repair?.challenges) ? rma.repair.challenges.filter(Boolean) : [];
    return [...entries].sort((a, b) => {
      const roundA = Number(a?.round) || 0;
      const roundB = Number(b?.round) || 0;
      return roundA - roundB;
    });
  }, [rma.repair?.challenges]);
  const latestChallenge = sortedChallenges.length > 0 ? sortedChallenges[sortedChallenges.length - 1] : null;
  const previousChallenges = sortedChallenges.slice(0, -1);
  const latestChallengeRound = Number(latestChallenge?.round) || Number(rma.repair?.challengeRound) || 0;
  const latestChallengeSubmittedAt = latestChallenge?.submittedAt ? formatDateTime(latestChallenge.submittedAt) : '';
  const latestChallengeSubmittedBy = latestChallenge?.submittedBy || '';
  const latestChallengeEmail = latestChallenge?.email || '';
  const latestChallengeFiles = Array.isArray(latestChallenge?.files) ? latestChallenge.files : [];
  const latestChallengeReview = latestChallenge?.review || null;
  const latestChallengeOutcome = latestChallengeReview?.outcome || '';
  const latestChallengeReviewedAt = latestChallengeReview?.decidedAt ? formatDateTime(latestChallengeReview.decidedAt) : '';
  const latestChallengeReviewer = latestChallengeReview?.decidedBy || '';
  const latestChallengeStaffNote = latestChallengeReview?.staffNote || '';
  const hasActiveChallenge = Boolean(latestChallenge && !latestChallengeOutcome);
  const formattedQuote = (() => {
    const amountValue = Number(quoteAmount || 0);
    if (!Number.isFinite(amountValue)) {
      return `${quoteCurrency} ${quoteAmount || '0'}`;
    }
    try {
      return new Intl.NumberFormat('no-NO', {
        style: 'currency',
        currency: quoteCurrency
      }).format(amountValue);
    } catch (err) {
      return `${quoteCurrency} ${amountValue.toFixed(2)}`;
    }
  })();

  const syncFromRepair = useCallback(repairPayload => {
    const repairData = repairPayload && typeof repairPayload === 'object' ? repairPayload : {};
    const normalized = normalizeTasks(repairData.tasks);
    setTasks(normalized.length ? normalized : [defaultTask()]);
    setSummary(repairData.summary || '');
    const recordedDecision = repairData.warranty?.decision || null;
    setWarrantyDecision(recordedDecision);
    setWarrantyNote(repairData.warranty?.note || '');
    setWarrantyLocked(Boolean(repairData.warranty?.decision));
    setQuoteAmount(
      typeof repairData.quote?.amount === 'number' ? String(repairData.quote.amount) : ''
    );
    setRepairDays(
      typeof repairData.timing?.repairDays === 'number'
        ? String(repairData.timing.repairDays)
        : ''
    );
    setPartsInStock(repairData.timing?.parts?.inStock === false ? false : true);
    const etaSource = repairData.timing?.parts?.etaDate;
    const etaString =
      typeof etaSource === 'string'
        ? etaSource
        : typeof etaSource?.toISOString === 'function'
        ? etaSource.toISOString()
        : '';
    setPartsEtaDate(etaString ? etaString.slice(0, 10) : '');
    setExpectedCompletion(repairData.timing?.expectedCompletion || '');
    const challengeEntries = Array.isArray(repairData.challenges) ? repairData.challenges.filter(Boolean) : [];
    const sorted = [...challengeEntries].sort((a, b) => {
      const roundA = Number(a?.round) || 0;
      const roundB = Number(b?.round) || 0;
      return roundA - roundB;
    });
    const latest = sorted.length > 0 ? sorted[sorted.length - 1] : null;
    const latestReview = latest?.review || null;
    if (latest && (!latestReview || !latestReview.outcome)) {
      setChallengeDecision('');
      setChallengeStaffNote('');
    } else if (latestReview) {
      setChallengeDecision(latestReview.outcome || '');
      setChallengeStaffNote(latestReview.staffNote || '');
    } else {
      setChallengeDecision('');
      setChallengeStaffNote('');
    }
    setChallengeError('');
    setChallengeFeedback('');
    setInspectionError('');
    setTasksDirty(false);
    setTasksSavedFeedback('');
  }, []);

  const resolveInspectionError = useCallback(code => {
    switch (code) {
      case 'INVALID_QUOTE_AMOUNT':
        return 'Enter a valid repair quote amount.';
      case 'INVALID_REPAIR_DAYS':
        return 'Enter the estimated repair time in days.';
      case 'ETA_REQUIRED':
        return 'Provide an ETA date when parts are not in stock.';
      case 'INVALID_ETA_DATE':
        return 'Enter a valid ETA date.';
      default:
        return 'Unable to finalize inspection.';
    }
  }, []);

  const resolveChallengeError = useCallback(code => {
    switch (code) {
      case 'REVIEW_NOT_ALLOWED':
        return 'Challenge review is not available right now.';
      case 'INVALID_OUTCOME':
        return 'Select a decision before submitting.';
      case 'NOTE_TOO_LONG':
        return 'Staff note must be 500 characters or fewer.';
      case 'ROUND_REQUIRED':
        return 'Challenge round information is missing.';
      case 'ROUND_NOT_FOUND':
        return 'Unable to locate this challenge round.';
      case 'CHALLENGE_ALREADY_REVIEWED':
        return 'This challenge round has already been reviewed.';
      default:
        return 'Unable to record challenge decision.';
    }
  }, []);

  useEffect(() => {
    syncFromRepair(rma.repair);
  }, [rma, syncFromRepair]);

  const handleStartRepair = async () => {
    try {
      setSaving(true);
      const token = await getToken();
      const res = await fetch(`${API_BASE}/rma/${rma._id}/repair/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        throw new Error('Failed to start repair');
      }
      const data = await res.json();
      const repairPayload = data.repair || {};
      syncFromRepair(repairPayload);
      const updated = {
        ...rma,
        status: data.nextStatus,
        repair: {
          ...(rma.repair || {}),
          ...repairPayload
        }
      };
      setFeedback('Repair started.');
      onSave(updated, { keepOpen: true });
    } catch (err) {
      console.error(err);
      setFeedback('Unable to start repair. Please retry.');
    } finally {
      setSaving(false);
    }
  };

  const fetchAiSuggestion = async () => {
    if (aiLoading) return;
    setAiLoading(true);
    setAiSuggestion(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/knowledge/suggest`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          symptom:          rma.description || '',
          productNumber:    rma.productNumber || '',
          serialNumber:     rma.serialNumber || '',
          repairCategories: faultCategories,
          assessmentNotes:  assessmentNotes.trim(),
          firmwareVersion:  firmwareVersion.trim(),
        }),
      });
      const data = await res.json();
      if (data.ok && data.suggestion) setAiSuggestion({ ...data.suggestion, similarCases: data.similarCases });
      else setAiSuggestion({ error: data.message || data.error || 'No suggestion available.' });
    } catch (err) {
      setAiSuggestion({ error: 'Could not reach suggestion service.' });
    } finally {
      setAiLoading(false);
    }
  };

  const handleConfirmWarranty = async () => {
    if (!warrantyDecision) {
      setFeedback('Select a warranty decision before confirming.');
      return;
    }
    try {
      setSaving(true);
      const token = await getToken();
      const res = await fetch(`${API_BASE}/rma/${rma._id}/repair/warranty`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ decision: warrantyDecision, note: warrantyNote })
      });
      if (!res.ok) {
        throw new Error('Failed to save warranty decision');
      }
      const data = await res.json();
      const updated = {
        ...rma,
        repair: {
          ...rma.repair,
          warranty: {
            ...(rma.repair?.warranty || {}),
            decision: data.warranty.decision,
            note: warrantyNote
          }
        }
      };
      syncFromRepair(updated.repair);
      setWarrantyLocked(true);
      setFeedback('Warranty decision recorded.');
      onSave(updated, { keepOpen: true });
    } catch (err) {
      console.error(err);
      setFeedback('Unable to record warranty decision.');
    } finally {
      setSaving(false);
      setShowWarrantyConfirm(false);
    }
  };

  const handleChallengeReview = async () => {
    if (!latestChallengeRound) {
      setChallengeError('Challenge round is unavailable.');
      return;
    }
    if (!challengeDecision) {
      setChallengeError('Select a decision before submitting.');
      return;
    }
    if (challengeStaffNote.length > 500) {
      setChallengeError('Staff note must be 500 characters or fewer.');
      return;
    }

    try {
      setChallengeSaving(true);
      setChallengeError('');
      setChallengeFeedback('');
      const token = await getToken();
      const payload = {
        outcome: challengeDecision,
        staffNote: challengeStaffNote.trim(),
        round: latestChallengeRound
      };
      const res = await fetch(`${API_BASE}/rma/${rma._id}/challenge/review`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const message = resolveChallengeError(body?.error);
        setChallengeError(message);
        return;
      }
      const data = await res.json();
      const repairPayload = data.repair || {};
      syncFromRepair(repairPayload);
      const updated = {
        ...rma,
        repair: {
          ...(rma.repair || {}),
          ...repairPayload
        }
      };
      setChallengeFeedback(
        challengeDecision === 'accepted'
          ? 'Warranty accepted — proceeding to execution.'
          : 'Challenge declined. Awaiting customer response.'
      );
      onSave(updated, { keepOpen: true });
    } catch (err) {
      console.error(err);
      setChallengeError('Unable to record challenge decision.');
    } finally {
      setChallengeSaving(false);
    }
  };

  const handleFinalizeInspection = async () => {
    if (faultCategories.length === 0) {
      setInspectionError('Select at least one fault category before finalizing.');
      return;
    }
    if (assessmentNotes.trim().length < 50) {
      setInspectionError('Assessment notes must be at least 50 characters — describe what you found in detail.');
      return;
    }
    const amountValue = Number(quoteAmount || 0);
    if (!Number.isFinite(amountValue) || amountValue < 0) {
      setInspectionError('Enter a valid repair quote amount.');
      return;
    }
    const repairDaysValue = Number(repairDays || 0);
    if (!Number.isFinite(repairDaysValue) || repairDaysValue < 0) {
      setInspectionError('Enter the estimated repair time in days.');
      return;
    }
    if (!partsInStock && !partsEtaDate) {
      setInspectionError('Provide an ETA date when parts are not in stock.');
      return;
    }

    try {
      setSaving(true);
      setInspectionError('');
      const token = await getToken();

      // Persist assessment notes and fault categories to the RMA
      await fetch(`${API_BASE}/rma/${rma._id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ assessmentNotes: assessmentNotes.trim(), repairCategories: faultCategories, firmwareVersion: firmwareVersion.trim(), environmentCode: environmentCode || '', 'outcome.failureMode': failureMode || '' }),
      });

      const inspectionPayload = {
        quote: { currency: quoteCurrency, amount: amountValue },
        timing: {
          repairDays: repairDaysValue,
          parts: partsInStock
            ? { inStock: true }
            : {
                inStock: false,
                etaDate: partsEtaDate
              }
        }
      };

      const inspectionRes = await fetch(`${API_BASE}/rma/${rma._id}/repair/inspection`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(inspectionPayload)
      });
      if (!inspectionRes.ok) {
        const errorBody = await inspectionRes.json().catch(() => ({}));
        const message = resolveInspectionError(errorBody?.error);
        setInspectionError(message);
        return;
      }
      const inspectionData = await inspectionRes.json();

      const finalizeRes = await fetch(
        `${API_BASE}/rma/${rma._id}/repair/inspection/finalize`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (!finalizeRes.ok) {
        const errorBody = await finalizeRes.json().catch(() => ({}));
        const message = resolveInspectionError(errorBody?.error);
        setInspectionError(message);
        return;
      }
      const finalizeData = await finalizeRes.json();
      const mergedRepair = {
        ...(inspectionData.repair || {}),
        ...(finalizeData.repair || {})
      };
      syncFromRepair(mergedRepair);
      const updated = {
        ...rma,
        repair: {
          ...(rma.repair || {}),
          ...mergedRepair
        }
      };
      setFeedback('Inspection finalized.');
      onSave(updated, { keepOpen: true });
    } catch (err) {
      console.error(err);
      setFeedback('Unable to finalize inspection.');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmPayment = async () => {
    try {
      setPaymentSaving(true);
      const token = await getToken();
      const res = await fetch(`${API_BASE}/rma/${rma._id}/repair/payment/confirm`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error('Failed to confirm payment');
      const data = await res.json();
      const updated = { ...rma, repair: { ...(rma.repair || {}), ...data.rma?.repair } };
      onSave(updated, { keepOpen: true });
    } catch (err) {
      console.error(err);
      setFeedback('Unable to confirm payment.');
    } finally {
      setPaymentSaving(false);
    }
  };

  const handleDeviceState = async (serialNumber, state) => {
    setDeviceSaving(serialNumber);
    setFeedback('');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/rma/${rma._id}/devices/repair`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ serialNumber, state }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setFeedback(data.message || 'Could not update that unit.');
        return;
      }
      // Keep the modal open — the technician works through the units in one go.
      onSave(data.rma, { keepOpen: true });
    } catch (err) {
      setFeedback('Could not update that unit.');
    } finally {
      setDeviceSaving('');
    }
  };

  const handleFinalize = async () => {
    try {
      setSaving(true);
      const token = await getToken();
      const payload = {
        tasks: sanitizeTasksForSubmit(tasks.length ? tasks : [defaultTask()]),
        summary: summary.trim()
      };
      const res = await fetch(`${API_BASE}/rma/${rma._id}/repair/finalize`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        throw new Error('Failed to finalize repair');
      }
      const data = await res.json();
      syncFromRepair(data.repair);
      const updated = {
        ...rma,
        status: data.nextStatus,
        repair: {
          ...rma.repair,
          ...data.repair
        }
      };
      setFeedback('Repair finalized.');
      onSave(updated);
    } catch (err) {
      console.error(err);
      setFeedback('Unable to finalize repair.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTasks = async () => {
    const validTasks = sanitizeTasksForSubmit(tasks);
    if (!validTasks.length) return;
    try {
      setTasksSaving(true);
      setTasksSavedFeedback('');
      const token = await getToken();
      const res = await fetch(`${API_BASE}/rma/${rma._id}/repair/tasks`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tasks: validTasks })
      });
      if (!res.ok) throw new Error('Save failed');
      const data = await res.json();
      const updated = { ...rma, repair: { ...(rma.repair || {}), ...data.repair } };
      setTasksDirty(false);
      setTasksSavedFeedback('Tasks saved.');
      setTimeout(() => setTasksSavedFeedback(''), 3000);
      onSave(updated, { keepOpen: true });
    } catch (err) {
      console.error(err);
      setTasksSavedFeedback('Could not save tasks.');
    } finally {
      setTasksSaving(false);
    }
  };

  const updateTask = (index, changes) => {
    setTasksDirty(true);
    setTasks(prev => {
      const next = [...prev];
      const updatedTask = { ...next[index], ...changes };
      // When marking done, record completedAt
      if ('done' in changes) {
        updatedTask.completedAt = changes.done ? new Date().toISOString() : null;
      }
      next[index] = updatedTask;
      return next;
    });
  };

  const addTask = () => {
    setTasksDirty(true);
    setTasks(prev => [...prev, defaultTask()]);
  };

  const removeTask = index => {
    setTasksDirty(true);
    setTasks(prev => prev.filter((_, idx) => idx !== index));
    setTaskOtherInputs(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const allCategories = [...PREDEFINED_CATEGORIES, ...customCategories];

  const selectCategory = (index, category) => {
    updateTask(index, { label: category });
    setTaskOtherInputs(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const confirmOtherCategory = (index) => {
    const value = (taskOtherInputs[index] || '').trim();
    if (!value) return;
    updateTask(index, { label: value });
    if (!allCategories.includes(value)) {
      const updated = [...customCategories, value];
      setCustomCategories(updated);
      saveCustomCategories(updated);
    }
    setTaskOtherInputs(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  return (
    <div style={modalStyles.overlay}>
      <div style={{ ...modalStyles.modal, maxWidth: '720px' }}>
        <h2 style={{ marginBottom: '0.25rem' }}>
          {isAwaitingRepair ? 'AWAITING REPAIR START' : 'REPAIRING'}
        </h2>
        <p style={{ marginTop: 0, color: colors.textMuted }}>
          Serial {rma.serialNumber} · RMA {rma.rmaId}
        </p>
        <RmaCustomerSummary rma={rma} />

        {/* Issue type + description + pre-screening */}
        <section style={{ ...sectionStyles.container, display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          <h3 style={sectionStyles.heading}>Customer report</h3>

          {/* Issue type + badges row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
            <span style={{ fontSize: '0.78rem', color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Issue type:
            </span>
            {issueBadges.map(badge => (
              <span key={badge} style={badgeStyles.badge}>{badge}</span>
            ))}
          </div>

          {/* Description */}
          {rma.description ? (
            <div style={{
              backgroundColor: 'rgba(255,255,255,0.04)',
              border: `1px solid ${colors.panelBorder}`,
              borderRadius: '8px',
              padding: '0.65rem 0.75rem',
              fontSize: '0.88rem',
              color: colors.textBody,
              whiteSpace: 'pre-wrap',
              lineHeight: 1.55
            }}>
              {rma.description}
            </div>
          ) : (
            <p style={{ fontSize: '0.85rem', color: colors.textMuted, margin: 0 }}>No description provided.</p>
          )}

          <SubmissionImageGallery images={rma.submissionImages} rmaId={rma._id} canCategorize={true} />

          <RmaCaseImages
            rma={rma}
            stage="repair"
            title="Repair photos"
            helper="Document the fault, the work performed and the finished repair. Stored on the case and shown in the history."
            onSave={onSave}
          />

          {/* Pre-screening */}
          {rma.preScreened === true ? (
            <div style={{
              borderRadius: '8px',
              border: '1px solid rgba(70,187,137,0.3)',
              backgroundColor: 'rgba(70,187,137,0.06)',
              padding: '0.65rem 0.75rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.35rem'
            }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: colors.accent }}>
                Has been in contact with North Invent
              </span>
              {rma.screeningNotes ? (
                <span style={{ fontSize: '0.88rem', color: colors.textBody, whiteSpace: 'pre-wrap' }}>
                  {rma.screeningNotes}
                </span>
              ) : (
                <span style={{ fontSize: '0.82rem', color: colors.textMuted }}>No additional notes provided.</span>
              )}
            </div>
          ) : rma.preScreened === false ? (
            <div style={{
              borderRadius: '8px',
              border: '1px solid rgba(234,179,8,0.3)',
              backgroundColor: 'rgba(234,179,8,0.05)',
              padding: '0.5rem 0.75rem',
              fontSize: '0.85rem',
              color: '#fbbf24'
            }}>
              Customer has not been in contact with North Invent prior to this RMA.
            </div>
          ) : (
            <div style={{ fontSize: '0.82rem', color: colors.textMuted }}>Pre-screening status not recorded.</div>
          )}
        </section>

        {isAwaitingRepair ? (
          <>
            <p style={sectionStyles.subtitle}>
              Start the repair to move this device into the active queue.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                style={modalStyles.button}
                onClick={handleStartRepair}
                disabled={saving}
              >
                START REPAIR
              </button>
              <button style={modalStyles.buttonAlt} onClick={onClose} disabled={saving}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={badgeStyles.container}>
              <span style={badgeStyles.badge}>{stageLabel}</span>
              <span style={badgeStyles.badge}>{warrantyLabel}</span>
              {waitingForCustomer && (
                <span
                  style={{
                    ...badgeStyles.badge,
                    backgroundColor: colors.statusYellow,
                    color: colors.sectionBackground
                  }}
                >
                  Waiting for customer decision
                </span>
              )}
              {customerApproved && (
                <span
                  style={{
                    ...badgeStyles.badge,
                    backgroundColor: colors.success,
                    color: colors.background
                  }}
                >
                  Customer accepted
                </span>
              )}
            </div>

            {!isInspectionPhase && shipBlockers.length > 0 && (
              <div
                role="group"
                aria-label="Next step"
                style={{
                  margin: '0.25rem 0 1rem', padding: '0.8rem 0.9rem', borderRadius: 10,
                  border: '1px solid rgba(251,146,60,0.45)', background: 'rgba(251,146,60,0.09)',
                  display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
                }}
              >
                <div style={{ flex: 1, minWidth: '15rem' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#fb923c', marginBottom: '0.15rem' }}>
                    Next step
                  </div>
                  <div style={{ fontSize: '0.9rem', color: colors.textStrong, lineHeight: 1.5 }}>
                    {shipBlockers[0].text}
                  </div>
                  {shipBlockers.length > 1 && (
                    <div style={{ fontSize: '0.78rem', color: colors.textMuted, marginTop: '0.2rem' }}>
                      Then {shipBlockers.length - 1} more before this case can ship.
                    </div>
                  )}
                </div>
                {shipBlockers[0].onAction && (
                  <button
                    type="button"
                    onClick={shipBlockers[0].onAction}
                    disabled={shipBlockers[0].actionDisabled}
                    style={{
                      padding: '0.55rem 1.1rem', borderRadius: 8, border: 'none',
                      backgroundColor: shipBlockers[0].actionDisabled ? 'rgba(251,146,60,0.4)' : '#fb923c',
                      color: '#1a0a00', fontWeight: 700, fontSize: '0.9rem',
                      cursor: shipBlockers[0].actionDisabled ? 'not-allowed' : 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    {shipBlockers[0].actionLabel}
                  </button>
                )}
              </div>
            )}

            {sortedChallenges.length > 0 && (
              <section style={sectionStyles.container}>
                <div style={challengeStyles.headerRow}>
                  <h3 style={sectionStyles.heading}>
                    Customer Challenge — Round #{latestChallengeRound || 1}
                  </h3>
                  {latestChallengeOutcome === 'accepted' ? (
                    <span style={challengeStyles.statusPillAccepted}>
                      Warranty accepted — proceed to Execution (2/2)
                    </span>
                  ) : latestChallengeOutcome === 'declined' ? (
                    <span style={challengeStyles.statusPillDeclined}>
                      Challenge declined — awaiting customer response
                    </span>
                  ) : null}
                </div>
                <div style={challengeStyles.container}>
                  <div style={challengeStyles.meta}>
                    Submitted{latestChallengeSubmittedAt ? ` on ${latestChallengeSubmittedAt}` : ''}
                    {latestChallengeSubmittedBy ? ` by ${latestChallengeSubmittedBy}` : ''}
                    {latestChallengeEmail ? ` · ${latestChallengeEmail}` : ''}
                  </div>
                  <div style={challengeStyles.message}>
                    {latestChallenge?.message || 'No message provided.'}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: colors.textStrong, marginBottom: '0.35rem' }}>
                      Attachments
                    </div>
                    {latestChallengeFiles.length > 0 ? (
                      <div style={challengeStyles.attachments}>
                        {latestChallengeFiles.map((file, index) => {
                          const label = file?.name || file?.url || `Attachment ${index + 1}`;
                          if (file?.url) {
                            const href = file.url.startsWith('/') ? `${API_BASE}${file.url}` : file.url;
                            return (
                              <a
                                key={`${file.url}-${index}`}
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={challengeStyles.attachmentLink}
                              >
                                {label}
                              </a>
                            );
                          }
                          return (
                            <span key={`attachment-${index}`} style={challengeStyles.meta}>
                              {label}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={challengeStyles.meta}>No attachments provided.</div>
                    )}
                  </div>
                  {latestChallengeOutcome && (
                    <>
                      <div style={challengeStyles.meta}>
                        Reviewed{latestChallengeReviewedAt ? ` on ${latestChallengeReviewedAt}` : ''}
                        {latestChallengeReviewer ? ` by ${latestChallengeReviewer}` : ''}
                      </div>
                      {latestChallengeStaffNote && (
                        <div
                          style={{
                            ...challengeStyles.message,
                            backgroundColor: 'rgba(0, 0, 0, 0.18)'
                          }}
                        >
                          Staff note: {latestChallengeStaffNote}
                        </div>
                      )}
                    </>
                  )}
                  {hasActiveChallenge && (
                    <div style={challengeStyles.decisionRow}>
                      <div style={{ fontWeight: 600, color: colors.textStrong }}>Decision</div>
                      <div style={challengeStyles.radioGroup}>
                        {['accepted', 'declined'].map(option => {
                          const isActive = challengeDecision === option;
                          return (
                            <label
                              key={option}
                              style={{
                                ...challengeStyles.radioOption,
                                ...(isActive ? challengeStyles.radioActive : {})
                              }}
                            >
                              <input
                                type="radio"
                                value={option}
                                checked={isActive}
                                onChange={() => setChallengeDecision(option)}
                                disabled={challengeSaving}
                              />
                              <span style={{ textTransform: 'capitalize' }}>{option}</span>
                            </label>
                          );
                        })}
                      </div>
                      <textarea
                        placeholder="Staff note (optional, max 500 characters)"
                        value={challengeStaffNote}
                        onChange={event => setChallengeStaffNote(event.target.value)}
                        style={challengeStyles.noteInput}
                        maxLength={500}
                        disabled={challengeSaving}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <button
                          type="button"
                          style={modalStyles.button}
                          onClick={handleChallengeReview}
                          disabled={challengeSaving}
                        >
                          {challengeSaving ? 'Saving…' : 'Save decision'}
                        </button>
                        {challengeFeedback && <span style={challengeStyles.feedback}>{challengeFeedback}</span>}
                        {challengeError && <span style={challengeStyles.error}>{challengeError}</span>}
                      </div>
                    </div>
                  )}
                  {!hasActiveChallenge && challengeFeedback && (
                    <div style={challengeStyles.feedback}>{challengeFeedback}</div>
                  )}
                  {!hasActiveChallenge && !challengeFeedback && challengeError && (
                    <div style={challengeStyles.error}>{challengeError}</div>
                  )}
                </div>
                {previousChallenges.length > 0 && (
                  <details style={challengeStyles.historyContainer}>
                    <summary style={challengeStyles.historySummary}>
                      Challenge history ({previousChallenges.length})
                    </summary>
                    <div style={challengeStyles.historyList}>
                      {[...previousChallenges].reverse().map((entry, index) => {
                        const submittedAt = entry?.submittedAt ? formatDateTime(entry.submittedAt) : '';
                        const review = entry?.review || {};
                        const outcome = review?.outcome || '';
                        const decidedAt = review?.decidedAt ? formatDateTime(review.decidedAt) : '';
                        const reviewer = review?.decidedBy || '';
                        const staffNote = review?.staffNote || '';
                        const files = Array.isArray(entry?.files) ? entry.files : [];
                        return (
                          <div
                            key={`history-${entry?.round || index}-${submittedAt || index}`}
                            style={challengeStyles.historyItem}
                          >
                            <div style={challengeStyles.historyHeader}>
                              Round #{entry?.round || '?'}{outcome ? ` — ${outcome}` : ''}
                            </div>
                            <div style={challengeStyles.historyMeta}>
                              Submitted{submittedAt ? ` on ${submittedAt}` : ''}
                              {entry?.submittedBy ? ` by ${entry.submittedBy}` : ''}
                              {entry?.email ? ` · ${entry.email}` : ''}
                            </div>
                            <div style={challengeStyles.historyNote}>
                              {entry?.message || 'No message provided.'}
                            </div>
                            {files.length > 0 && (
                              <div style={challengeStyles.historyMeta}>
                                Attachments:{' '}
                                {files.map((file, fileIndex) => {
                                  const label = file?.name || file?.url || `Attachment ${fileIndex + 1}`;
                                  if (file?.url) {
                                    return (
                                      <a
                                        key={`${file.url}-${fileIndex}`}
                                        href={file.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          ...challengeStyles.attachmentLink,
                                          display: 'inline-block',
                                          marginRight: '0.5rem'
                                        }}
                                      >
                                        {label}
                                      </a>
                                    );
                                  }
                                  return (
                                    <span key={`history-attachment-${fileIndex}`} style={challengeStyles.meta}>
                                      {label}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                            {outcome && (
                              <div style={challengeStyles.historyMeta}>
                                Reviewed{decidedAt ? ` on ${decidedAt}` : ''}
                                {reviewer ? ` by ${reviewer}` : ''}
                              </div>
                            )}
                            {staffNote && (
                              <div style={challengeStyles.historyNote}>Staff note: {staffNote}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </details>
                )}
              </section>
            )}

            <section style={sectionStyles.container}>
              <h3 style={sectionStyles.heading}>Warranty decision</h3>
              <div style={warrantyStyles.options}>
                {['accepted', 'denied'].map(option => {
                  const isActive = warrantyDecision === option;
                  return (
                    <label
                      key={option}
                      style={{
                        ...warrantyStyles.option,
                        ...(isActive ? warrantyStyles.optionActive : {}),
                        cursor: warrantyLocked ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <input
                        type="radio"
                        name="warranty-decision"
                        value={option}
                        checked={isActive}
                        disabled={warrantyLocked}
                        onChange={() => setWarrantyDecision(option)}
                      />
                      <span style={{ textTransform: 'capitalize' }}>{option}</span>
                    </label>
                  );
                })}
              </div>
              {!warrantyLocked && (
                <>
                  <textarea
                    style={warrantyStyles.note}
                    placeholder="Optional note"
                    value={warrantyNote}
                    onChange={e => setWarrantyNote(e.target.value)}
                  />
                  {warrantyNote && (
                    <div style={{ fontSize: '0.75rem', color: 'rgba(251,146,60,0.8)', marginTop: '0.2rem' }}>
                      Visible to customer — keep it professional and clear.
                    </div>
                  )}
                </>
              )}
              <button
                style={{ ...modalStyles.button, opacity: warrantyLocked ? 0.6 : 1 }}
                onClick={() => {
                  if (!warrantyDecision) { setFeedback('Select a warranty decision before confirming.'); return; }
                  setShowWarrantyConfirm(true);
                }}
                disabled={warrantyLocked || saving}
              >
                Lock &amp; Confirm Warranty Decision
              </button>
              {warrantyLocked && (
                <p style={warrantyStyles.lockNote}>
                  🔒 Warranty <strong>{warrantyDecisionValue}</strong> — locked{rma.repair?.warranty?.decidedBy ? ` by ${rma.repair.warranty.decidedBy}` : ''}. This decision is final and cannot be changed.
                </p>
              )}

              {showWarrantyConfirm && (
                <div
                  onClick={() => !saving && setShowWarrantyConfirm(false)}
                  style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.66)', zIndex: 4000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
                  <div onClick={e => e.stopPropagation()}
                    style={{ background: 'var(--ni-bg, #14181c)', border: '1px solid #EC212466', borderRadius: 12,
                      padding: '1.5rem', maxWidth: 460, width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,0.5)' }}>
                    <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.05rem', color: '#EC2124' }}>⚠ Lock the warranty decision?</h3>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.9rem', lineHeight: 1.5, color: 'var(--ni-text, #e8eaed)' }}>
                      You are about to record the warranty as{' '}
                      <strong style={{ textTransform: 'uppercase' }}>{warrantyDecision}</strong>. Once confirmed this is
                      <strong> final and cannot be changed</strong> — not by you, not by anyone. The customer is told this outcome,
                      so a later reversal is not possible.
                    </p>
                    <p style={{ margin: '0 0 1.1rem', fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)' }}>
                      Make sure the decision is correct before you continue.
                    </p>
                    <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
                      <button onClick={() => setShowWarrantyConfirm(false)} disabled={saving}
                        style={{ background: 'transparent', border: '1px solid var(--ni-border-soft, #2a3038)',
                          color: 'var(--ni-text, #e8eaed)', borderRadius: 8, padding: '0.5rem 1rem', cursor: 'pointer' }}>
                        Cancel
                      </button>
                      <button onClick={handleConfirmWarranty} disabled={saving}
                        style={{ background: '#EC2124', color: '#fff', border: 'none', borderRadius: 8,
                          padding: '0.5rem 1.1rem', fontWeight: 700, cursor: saving ? 'wait' : 'pointer' }}>
                        {saving ? 'Locking…' : `Lock in “${warrantyDecision}”`}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </section>

            {inspectionAwaitingCustomer ? (
              <section style={sectionStyles.container}>
                <h3 style={sectionStyles.heading}>Inspection finalized</h3>
                <div style={{
                  borderRadius: '10px', border: '1px solid rgba(251,191,36,0.4)',
                  backgroundColor: 'rgba(251,191,36,0.07)', padding: '0.85rem',
                  display: 'flex', flexDirection: 'column', gap: '0.5rem'
                }}>
                  <span style={{ fontWeight: 700, color: '#fbbf24', fontSize: '0.92rem' }}>
                    Waiting for the customer to accept the quote
                  </span>
                  <span style={{ fontSize: '0.85rem', color: colors.textBody, lineHeight: 1.55 }}>
                    The warranty was denied, so this repair is chargeable and the customer
                    has to approve it before work can start. Nothing more is needed from
                    you until they answer.
                  </span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem', marginTop: '0.2rem' }}>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Quote</div>
                      <div style={{ fontSize: '0.95rem', color: colors.textStrong, fontWeight: 600 }}>{formattedQuote}</div>
                    </div>
                    {repairDays !== '' && repairDays !== null && (
                      <div>
                        <div style={{ fontSize: '0.72rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Estimated repair</div>
                        <div style={{ fontSize: '0.95rem', color: colors.textStrong, fontWeight: 600 }}>{repairDays} day{Number(repairDays) === 1 ? '' : 's'}</div>
                      </div>
                    )}
                    <div>
                      <div style={{ fontSize: '0.72rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Finalized</div>
                      <div style={{ fontSize: '0.95rem', color: colors.textStrong, fontWeight: 600 }}>
                        {formatDateTime(inspectionFinalizedAt)}{inspectionFinalizedBy ? ` · ${inspectionFinalizedBy}` : ''}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            ) : isInspectionPhase ? (
              <section style={sectionStyles.container}>
                <h3 style={sectionStyles.heading}>Inspection details</h3>
                {/* Fault categories — required */}
                <div style={{ ...inspectionFormStyles.row, padding: '0.75rem', borderRadius: '8px', border: `1px solid rgba(70,187,137,0.35)`, background: 'rgba(70,187,137,0.05)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <label style={{ ...inspectionFormStyles.label, color: colors.primary }}>Fault Categories</label>
                    <span style={{ fontSize: '0.72rem', color: colors.danger, fontWeight: 700, letterSpacing: '0.05em' }}>REQUIRED</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {FAULT_CATEGORIES.map(cat => {
                      const active = faultCategories.includes(cat.value);
                      return (
                        <button key={cat.value} type="button" disabled={saving}
                          onClick={() => setFaultCategories(prev => active ? prev.filter(v => v !== cat.value) : [...prev, cat.value])}
                          style={{ padding: '0.28rem 0.65rem', borderRadius: 999, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', border: `1px solid ${active ? colors.primary : colors.panelBorder}`, background: active ? 'rgba(70,187,137,0.18)' : 'rgba(255,255,255,0.04)', color: active ? colors.primary : colors.textMuted, transition: 'all 0.15s' }}
                        >{cat.label}</button>
                      );
                    })}
                  </div>
                  <p style={{ ...inspectionFormStyles.hint, marginTop: '0.4rem' }}>Select all areas affected. This is used for fault trend analysis.</p>
                </div>

                {/* Firmware version — optional */}
                <div style={inspectionFormStyles.row}>
                  <label style={inspectionFormStyles.label}>Firmware Version</label>
                  <input
                    type="text"
                    value={firmwareVersion}
                    onChange={e => setFirmwareVersion(e.target.value)}
                    disabled={saving}
                    placeholder="e.g. 2.4.1 or v1.0.3-rc2"
                    style={inspectionFormStyles.input}
                  />
                  <p style={inspectionFormStyles.hint}>Record the firmware version running on the device. Used for software fault correlation analysis.</p>
                </div>

                {/* Environment + Failure mode — optional */}
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <div style={{ ...inspectionFormStyles.row, flex: 1, minWidth: 180 }}>
                    <label style={inspectionFormStyles.label}>Environment <span style={{ fontSize: '0.7rem', color: colors.textMuted, fontWeight: 400 }}>(optional)</span></label>
                    <select value={environmentCode} onChange={e => setEnvironmentCode(e.target.value)} disabled={saving}
                      style={{ ...inspectionFormStyles.input, cursor: 'pointer' }}>
                      <option value="">— not set —</option>
                      <option value="marine">Marine</option>
                      <option value="industrial">Industrial</option>
                      <option value="office">Office</option>
                      <option value="outdoor">Outdoor</option>
                      <option value="other">Other</option>
                    </select>
                    <p style={inspectionFormStyles.hint}>Where is the device operating? Used for environment-related fault analysis.</p>
                  </div>
                  <div style={{ ...inspectionFormStyles.row, flex: 1, minWidth: 180 }}>
                    <label style={inspectionFormStyles.label}>Failure Mode <span style={{ fontSize: '0.7rem', color: colors.textMuted, fontWeight: 400 }}>(optional)</span></label>
                    <select value={failureMode} onChange={e => setFailureMode(e.target.value)} disabled={saving}
                      style={{ ...inspectionFormStyles.input, cursor: 'pointer' }}>
                      <option value="">— not set —</option>
                      <option value="total">Total — completely dead</option>
                      <option value="intermittent">Intermittent — comes and goes</option>
                      <option value="partial">Partial — partially working</option>
                    </select>
                    <p style={inspectionFormStyles.hint}>How the fault manifests. Intermittent failures are harder to diagnose and tend to recur.</p>
                  </div>
                </div>

                {/* Assessment notes — required */}
                <div style={{ ...inspectionFormStyles.row, padding: '0.75rem', borderRadius: '8px', border: `1px solid rgba(70,187,137,0.35)`, background: 'rgba(70,187,137,0.05)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <label style={{ ...inspectionFormStyles.label, color: colors.primary }}>Assessment Notes</label>
                    <span style={{ fontSize: '0.72rem', color: colors.danger, fontWeight: 700, letterSpacing: '0.05em' }}>REQUIRED</span>
                  </div>
                  <textarea
                    value={assessmentNotes}
                    onChange={e => setAssessmentNotes(e.target.value)}
                    disabled={saving}
                    placeholder="Describe what you found during inspection — symptoms, test results, condition of the unit, any error codes observed…"
                    rows={4}
                    style={{ ...inputBase, minHeight: '90px', resize: 'vertical' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                    <p style={{ ...inspectionFormStyles.hint, margin: 0 }}>Minimum 50 characters. Stored permanently for fault pattern analysis.</p>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: assessmentNotes.trim().length >= 50 ? colors.primary : colors.danger }}>
                      {assessmentNotes.trim().length}/50
                    </span>
                  </div>
                </div>

                {/* AI suggestion panel */}
                <div style={{ borderRadius: 8, border: '1px solid rgba(70,187,137,0.25)', background: 'rgba(70,187,137,0.04)', padding: '0.85rem', marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '0.8rem', fontWeight: 700, color: colors.primary, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                        AI Diagnosis Suggestion
                      </span>
                      <span style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.35)', color: '#fbbf24', borderRadius: 999, padding: '1px 7px' }}>
                        Experimental
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {aiSuggestion && !aiSuggestion.error && (
                        <button
                          type="button"
                          onClick={() => {
                            if (aiSuggestion.suggestedCategories?.length) {
                              const valid = aiSuggestion.suggestedCategories.filter(v => FAULT_CATEGORIES.some(f => f.value === v));
                              if (valid.length) setFaultCategories(prev => [...new Set([...prev, ...valid])]);
                            }
                            if (aiSuggestion.estimatedDurationDays) setRepairDays(String(aiSuggestion.estimatedDurationDays));
                          }}
                          style={{ padding: '0.3rem 0.8rem', borderRadius: 6, border: '1px solid rgba(70,187,137,0.4)', background: 'rgba(70,187,137,0.18)', color: colors.primary, fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}
                        >
                          Apply to form
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={fetchAiSuggestion}
                        disabled={aiLoading || saving}
                        style={{ padding: '0.3rem 0.8rem', borderRadius: 6, border: `1px solid ${colors.primary}`, background: 'rgba(70,187,137,0.12)', color: colors.primary, fontSize: '0.78rem', fontWeight: 700, cursor: aiLoading ? 'wait' : 'pointer', opacity: (aiLoading || saving) ? 0.6 : 1 }}
                      >
                        {aiLoading ? 'Analysing…' : aiSuggestion ? 'Refresh' : 'Get suggestion'}
                      </button>
                    </div>
                  </div>

                  {!aiSuggestion && !aiLoading && (
                    <p style={{ margin: '0 0 0.6rem', fontSize: '0.75rem', color: colors.textMuted, lineHeight: 1.5 }}>
                      Each request queries OpenAI and uses a small number of API tokens (~$0.001 per call). Use once you have filled in the relevant fields above — avoid refreshing repeatedly.
                    </p>
                  )}

                  {aiSuggestion && !aiSuggestion.error && (
                    <div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '0.78rem', background: 'rgba(70,187,137,0.15)', border: '1px solid rgba(70,187,137,0.4)', color: colors.primary, borderRadius: 20, padding: '2px 10px', fontWeight: 700 }}>
                          {aiSuggestion.rootCause} · {aiSuggestion.rootCauseConfidence}
                        </span>
                        <span style={{ fontSize: '0.78rem', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: colors.textBody, borderRadius: 20, padding: '2px 10px' }}>
                          → {aiSuggestion.resolution} · {aiSuggestion.resolutionConfidence}
                        </span>
                        <span style={{ fontSize: '0.78rem', borderRadius: 20, padding: '2px 10px', fontWeight: 700, border: '1px solid', ...(aiSuggestion.warrantyLikelihood === 'likely' ? { background: 'rgba(70,187,137,0.12)', borderColor: 'rgba(70,187,137,0.4)', color: colors.primary } : aiSuggestion.warrantyLikelihood === 'unlikely' ? { background: 'rgba(248,113,113,0.1)', borderColor: 'rgba(248,113,113,0.4)', color: '#f87171' } : { background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.12)', color: colors.textMuted }) }}>
                          warranty: {aiSuggestion.warrantyLikelihood}
                        </span>
                        {aiSuggestion.estimatedDurationDays && (
                          <span style={{ fontSize: '0.78rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: colors.textMuted, borderRadius: 20, padding: '2px 10px' }}>
                            ~{aiSuggestion.estimatedDurationDays}d repair
                          </span>
                        )}
                      </div>
                      {aiSuggestion.suggestedCategories?.length > 0 && (
                        <p style={{ margin: '0 0 0.4rem', fontSize: '0.78rem', color: colors.textMuted }}>
                          Suggested categories: <strong style={{ color: colors.textBody }}>{aiSuggestion.suggestedCategories.join(', ')}</strong>
                        </p>
                      )}
                      {aiSuggestion.suggestedParts?.length > 0 && (
                        <p style={{ margin: '0 0 0.4rem', fontSize: '0.78rem', color: colors.textMuted }}>
                          Likely parts: <strong style={{ color: colors.textBody }}>{aiSuggestion.suggestedParts.join(', ')}</strong>
                        </p>
                      )}
                      <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', color: colors.textBody, lineHeight: 1.5 }}>{aiSuggestion.reasoning}</p>
                      {aiSuggestion.similarCases?.length > 0 && (
                        <p style={{ margin: 0, fontSize: '0.75rem', color: colors.textMuted }}>
                          Based on: {aiSuggestion.similarCases.map(c => `${c.rmaId} (${c.score}%)`).join(', ')}
                        </p>
                      )}
                    </div>
                  )}

                  {aiSuggestion?.error && (
                    <p style={{ margin: 0, fontSize: '0.8rem', color: colors.textMuted }}>{aiSuggestion.error}</p>
                  )}
                </div>

                <div style={inspectionFormStyles.row}>
                  <label style={inspectionFormStyles.label}>
                    Service quote ({quoteCurrency})
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={quoteAmount}
                    onChange={e => setQuoteAmount(e.target.value)}
                    style={{
                      ...inspectionFormStyles.input,
                      border: warrantyDecision === 'denied'
                        ? '1.5px solid rgba(248,113,113,0.7)'
                        : '1.5px solid rgba(70,187,137,0.35)'
                    }}
                    disabled={saving}
                  />
                  {warrantyDecision === 'denied' ? (
                    <p style={{ ...inspectionFormStyles.hint, color: '#f87171', marginTop: '0.25rem' }}>
                      Warranty denied — this cost will be presented to the customer. Enter the exact amount.
                    </p>
                  ) : warrantyDecision === 'accepted' ? (
                    <p style={{ ...inspectionFormStyles.hint, color: colors.textMuted, marginTop: '0.25rem' }}>
                      Warranty accepted — internal cost tracking only. Enter actual service cost.
                    </p>
                  ) : (
                    <p style={{ ...inspectionFormStyles.hint, color: colors.textMuted, marginTop: '0.25rem' }}>
                      Confirm warranty decision above before entering the quote.
                    </p>
                  )}
                </div>
                <div style={{
                  ...inspectionFormStyles.row,
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid rgba(99,179,237,0.35)',
                  backgroundColor: 'rgba(99,179,237,0.06)'
                }}>
                  <label style={{ ...inspectionFormStyles.label, color: '#63b3ed' }}>
                    Estimated service time (days)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={repairDays}
                    onChange={e => setRepairDays(e.target.value)}
                    style={{ ...inspectionFormStyles.input, border: '1.5px solid rgba(99,179,237,0.5)' }}
                    disabled={saving}
                  />
                  <p style={{ ...inspectionFormStyles.hint, marginTop: '0.25rem' }}>
                    Your estimate is shown to the customer as expected service duration.
                  </p>
                </div>
                <div style={inspectionFormStyles.inline}>
                  <label
                    style={{
                      ...inspectionFormStyles.label,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={partsInStock}
                      onChange={e => setPartsInStock(e.target.checked)}
                      disabled={saving}
                    />
                    Parts in stock
                  </label>
                  {!partsInStock && (
                    <>
                      <label style={inspectionFormStyles.label} htmlFor="inspection-eta">
                        ETA
                      </label>
                      <input
                        id="inspection-eta"
                        type="date"
                        value={partsEtaDate}
                        onChange={e => setPartsEtaDate(e.target.value)}
                        style={inspectionFormStyles.input}
                        disabled={saving}
                      />
                    </>
                  )}
                </div>
                {!partsInStock && partsEtaDate && (
                  <p style={inspectionFormStyles.hint}>
                    Parts arrive in {formWaitDays != null ? `${formWaitDays} days` : '—'}
                    {formExpectedCompletion ? ` · Expected completion: ${formExpectedCompletion}` : ''}
                  </p>
                )}
                {partsInStock && formExpectedCompletion && (
                  <p style={inspectionFormStyles.hint}>Expected completion: {formExpectedCompletion}</p>
                )}
                {inspectionError && (
                  <p style={{ ...inspectionFormStyles.hint, color: colors.danger }}>
                    {inspectionError}
                  </p>
                )}
                <button
                  style={modalStyles.button}
                  onClick={handleFinalizeInspection}
                  disabled={saving}
                >
                  Finalize inspection
                </button>
              </section>
            ) : (
              <>
                {paymentRequired && (
                  <section style={{ ...sectionStyles.container, marginBottom: '1rem' }}>
                    <h3 style={sectionStyles.heading}>Payment</h3>
                    {paymentReceived ? (
                      <div style={{
                        borderRadius: '10px', border: '1px solid rgba(70,187,137,0.3)',
                        backgroundColor: 'rgba(70,187,137,0.07)', padding: '0.75rem',
                        display: 'flex', flexDirection: 'column', gap: '0.3rem'
                      }}>
                        <span style={{ fontWeight: 700, color: colors.accent, fontSize: '0.9rem' }}>✓ Payment received</span>
                        {paymentReceivedAt && <span style={{ fontSize: '0.82rem', color: colors.textMuted }}>Confirmed {paymentReceivedAt}</span>}
                        {paymentData.receivedBy && <span style={{ fontSize: '0.82rem', color: colors.textMuted }}>By {paymentData.receivedBy}</span>}
                      </div>
                    ) : (
                      <div style={{
                        borderRadius: '10px', border: '1px solid rgba(251,146,60,0.4)',
                        backgroundColor: 'rgba(251,146,60,0.06)', padding: '0.75rem',
                        display: 'flex', flexDirection: 'column', gap: '0.5rem'
                      }}>
                        <span style={{ fontWeight: 700, color: '#fb923c', fontSize: '0.9rem' }}>Payment pending</span>
                        <span style={{ fontSize: '0.82rem', color: colors.textMuted }}>
                          Customer accepted the non-warranty repair.
                          {paymentData.amount != null && ` Amount: ${new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(paymentData.amount)}`}
                        </span>
                        {/* No button here. The same orange button sat a few
                            hundred pixels above in the Next step strip, which
                            reads as two payments rather than one thing to do.
                            This section is the record; the strip is the action. */}
                        <span style={{ fontSize: '0.78rem', color: colors.textMuted, fontStyle: 'italic' }}>
                          Confirm it under Next step at the top of this case.
                        </span>
                      </div>
                    )}
                  </section>
                )}
                <section style={sectionStyles.container}>
                  <h3 style={sectionStyles.heading}>Repair overview</h3>
                  <p style={sectionStyles.subtitle}>Quote: {formattedQuote}</p>
                  <p style={sectionStyles.subtitle}>
                    Repair time: {rma.repair?.timing?.repairDays ?? 0} days
                  </p>
                  <p style={sectionStyles.subtitle}>
                    {partsInStock
                      ? 'Parts in stock'
                      : `Waiting for parts${etaDisplay ? ` · ETA ${etaDisplay}` : ''}${
                          waitDays != null ? ` (${waitDays} days)` : ''
                        }`}
                  </p>
                  {expectedCompletionDisplay && (
                    <p style={sectionStyles.subtitle}>
                      Expected completion: {expectedCompletionDisplay}
                    </p>
                  )}
                  {customerApproved && (
                    <p style={{ ...inspectionFormStyles.hint, color: colors.success }}>
                      Customer accepted. Proceeding with repair.
                    </p>
                  )}
                  {customerApprovalStatus === 'declined' && (
                    <p style={{ ...inspectionFormStyles.hint, color: colors.danger }}>
                      Customer declined repair.
                    </p>
                  )}
                </section>

                <section style={sectionStyles.container}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h3 style={{ ...sectionStyles.heading, marginBottom: 0 }}>Repair tasks</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                      {tasksSavedFeedback && (
                        <span style={{ fontSize: '0.82rem', color: tasksSavedFeedback.includes('Could') ? colors.danger : colors.success }}>
                          {tasksSavedFeedback}
                        </span>
                      )}
                      {tasksDirty && !tasksSavedFeedback && (
                        <span style={{ fontSize: '0.8rem', color: colors.statusYellow }}>Unsaved changes</span>
                      )}
                      <button
                        type="button"
                        onClick={handleSaveTasks}
                        disabled={tasksSaving || !tasksDirty || !sanitizeTasksForSubmit(tasks).length}
                        style={{
                          padding: '0.35rem 0.85rem',
                          borderRadius: '999px',
                          border: `1px solid ${tasksDirty ? colors.accent : colors.panelBorder}`,
                          background: tasksDirty ? 'rgba(70,187,137,0.15)' : 'rgba(255,255,255,0.04)',
                          color: tasksDirty ? colors.accent : colors.textMuted,
                          cursor: tasksDirty ? 'pointer' : 'default',
                          fontWeight: 700,
                          fontSize: '0.82rem',
                          transition: 'all 0.15s'
                        }}
                      >
                        {tasksSaving ? 'Saving…' : 'Save Tasks'}
                      </button>
                    </div>
                  </div>
                  <div style={checklistStyles.container}>
                    {(tasks.length ? tasks : [defaultTask()]).map((task, index) => {
                      const isOtherMode = taskOtherInputs[index] !== undefined;
                      const isCurrentCategory = (cat) => task.label === cat && !isOtherMode;
                      const dueDateDisplay = task.dueDate ? formatDDMMYYYY(task.dueDate) : '';
                      return (
                        <div
                          key={index}
                          style={{
                            ...checklistStyles.taskRow,
                            borderColor: task.done ? 'rgba(70,187,137,0.3)' : colors.panelBorder,
                            opacity: task.done ? 0.75 : 1
                          }}
                        >
                          {/* Category picker */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.25rem' }}>
                            {allCategories.map(cat => (
                              <button
                                key={cat}
                                type="button"
                                onClick={() => selectCategory(index, cat)}
                                disabled={saving || task.done}
                                style={{
                                  padding: '0.25rem 0.65rem',
                                  borderRadius: '999px',
                                  fontSize: '0.78rem',
                                  fontWeight: 600,
                                  cursor: task.done ? 'default' : 'pointer',
                                  border: `1px solid ${isCurrentCategory(cat) ? colors.accent : colors.panelBorder}`,
                                  background: isCurrentCategory(cat) ? 'rgba(70,187,137,0.15)' : 'rgba(255,255,255,0.04)',
                                  color: isCurrentCategory(cat) ? colors.accent : colors.textMuted,
                                  transition: 'all 0.12s'
                                }}
                              >
                                {cat}
                              </button>
                            ))}
                            <button
                              type="button"
                              onClick={() => setTaskOtherInputs(prev => ({ ...prev, [index]: prev[index] !== undefined ? undefined : '' }))}
                              disabled={saving || task.done}
                              style={{
                                padding: '0.25rem 0.65rem',
                                borderRadius: '999px',
                                fontSize: '0.78rem',
                                fontWeight: 600,
                                cursor: task.done ? 'default' : 'pointer',
                                border: `1px solid ${isOtherMode ? colors.accent : colors.panelBorder}`,
                                background: isOtherMode ? 'rgba(70,187,137,0.15)' : 'rgba(255,255,255,0.04)',
                                color: isOtherMode ? colors.accent : colors.textMuted
                              }}
                            >
                              + Other
                            </button>
                          </div>

                          {isOtherMode && (
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <input
                                type="text"
                                placeholder="Custom category name…"
                                value={taskOtherInputs[index]}
                                onChange={e => setTaskOtherInputs(prev => ({ ...prev, [index]: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); confirmOtherCategory(index); } }}
                                style={{ ...checklistStyles.labelInput, flex: 1 }}
                                autoFocus
                                disabled={saving}
                              />
                              <button
                                type="button"
                                onClick={() => confirmOtherCategory(index)}
                                style={{ ...inputBase, width: 'auto', padding: '0.35rem 0.8rem', background: 'rgba(70,187,137,0.15)', color: colors.accent, border: `1px solid ${colors.accent}`, borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}
                                disabled={saving || !(taskOtherInputs[index] || '').trim()}
                              >
                                Use
                              </button>
                            </div>
                          )}

                          {/* Task label */}
                          <input
                            type="text"
                            placeholder="Task description"
                            value={task.label}
                            onChange={e => updateTask(index, { label: e.target.value })}
                            style={{
                              ...checklistStyles.labelInput,
                              textDecoration: task.done ? 'line-through' : 'none'
                            }}
                            disabled={saving || task.done}
                          />

                          {/* Due date row */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap' }}>
                            <label style={{ fontSize: '0.82rem', color: colors.textMuted, fontWeight: 600, whiteSpace: 'nowrap' }}>
                              Due date
                            </label>
                            <input
                              type="date"
                              value={task.dueDate || ''}
                              onChange={e => updateTask(index, { dueDate: e.target.value })}
                              style={{ ...inputBase, width: 'auto', fontSize: '0.82rem', padding: '0.3rem 0.55rem' }}
                              disabled={saving || task.done}
                            />
                            {dueDateDisplay && (
                              <span style={{ fontSize: '0.8rem', color: colors.textMuted }}>{dueDateDisplay}</span>
                            )}
                            {!task.dueDate && (
                              <span style={{ fontSize: '0.78rem', color: colors.statusYellow }}>Required before saving</span>
                            )}
                          </div>

                          <div style={checklistStyles.controls}>
                            <label style={checklistStyles.checkboxLabel}>
                              <input
                                type="checkbox"
                                checked={task.done}
                                onChange={e => updateTask(index, { done: e.target.checked })}
                                disabled={saving}
                              />
                              {task.done
                                ? `Completed${task.completedAt ? ` · ${formatDDMMYYYY(task.completedAt)}` : ''}`
                                : 'Mark as completed'}
                            </label>
                            {(tasks.length > 1 || task.label) && (
                              <button
                                type="button"
                                onClick={() => removeTask(index)}
                                style={checklistStyles.removeButton}
                                disabled={saving}
                              >
                                Remove
                              </button>
                            )}
                          </div>

                          <textarea
                            placeholder="Notes (optional)"
                            value={task.note}
                            onChange={e => updateTask(index, { note: e.target.value })}
                            style={checklistStyles.noteInput}
                            disabled={saving}
                          />
                          {task.note && (
                            <div style={{ fontSize: '0.75rem', color: 'rgba(251,146,60,0.8)', marginTop: '0.2rem' }}>
                              Visible to customer — keep it professional and clear.
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: '0.65rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={addTask}
                      style={modalStyles.buttonAlt}
                      disabled={saving}
                    >
                      Add Task
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveTasks}
                      disabled={tasksSaving || !tasksDirty || !sanitizeTasksForSubmit(tasks).length}
                      style={{
                        ...modalStyles.button,
                        opacity: (!tasksDirty || !sanitizeTasksForSubmit(tasks).length) ? 0.45 : 1
                      }}
                    >
                      {tasksSaving ? 'Saving…' : 'Save Tasks'}
                    </button>
                  </div>
                </section>

                {isBundle && (
                  <section style={sectionStyles.container}>
                    <h3 style={sectionStyles.heading}>
                      Units on this case
                      <span style={{
                        marginLeft: '0.5rem',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        color: devicesResolved ? '#46bb89' : '#fb923c',
                      }}>
                        {deviceProgress.done}/{deviceProgress.total} finished
                      </span>
                    </h3>
                    <p style={{ margin: '0 0 0.6rem', fontSize: '0.78rem', color: colors.textMuted }}>
                      Every unit must be finished before the case can be finalized and shipped.
                    </p>
                    {caseDevices.map(device => {
                      const state = device?.repair?.state || 'pending';
                      const done = state !== 'pending';
                      return (
                        <div key={device.serialNumber} style={deviceRowStyles.row}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: colors.textStrong }}>
                              {device.serialNumber}
                              {device.isPrimary && <span style={deviceRowStyles.primaryTag}>primary</span>}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: colors.textMuted }}>
                              {[device.productNumber, device.issueType].filter(Boolean).join(' · ')}
                            </div>
                            {device.description && (
                              <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginTop: '0.2rem' }}>
                                {device.description}
                              </div>
                            )}
                            {device.sourceRmaId && (
                              <div style={{ fontSize: '0.7rem', color: colors.textDim, marginTop: '0.2rem' }}>
                                bundled from {device.sourceRmaId}
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
                            <span style={{
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              color: done ? '#46bb89' : '#fb923c',
                            }}>
                              {REPAIR_STATE_LABELS[state] || state}
                            </span>
                            <select
                              value={state}
                              onChange={e => handleDeviceState(device.serialNumber, e.target.value)}
                              className="ni-select"
                              style={{ marginBottom: 0, fontSize: '0.78rem', minWidth: 150 }}
                              disabled={deviceSaving === device.serialNumber || saving}
                            >
                              <option value="pending">Not finished</option>
                              <option value="repaired">Repaired</option>
                              <option value="no_fault">No fault found</option>
                              <option value="scrapped">Scrapped</option>
                            </select>
                          </div>
                        </div>
                      );
                    })}
                  </section>
                )}

                <section style={sectionStyles.container}>
                  <h3 style={sectionStyles.heading}>Repair summary</h3>
                  <textarea
                    style={summaryStyles.textarea}
                    value={summary}
                    onChange={e => setSummary(e.target.value)}
                    placeholder="Brief summary of work performed"
                    disabled={saving}
                  />
                  <div style={{ fontSize: '0.75rem', color: 'rgba(251,146,60,0.8)', marginTop: '0.35rem' }}>
                    Visible to customer — keep it professional and clear.
                  </div>
                </section>

                {shipBlockers.length > 0 ? (
                  <div
                    role="group"
                    aria-label="Before this case can ship"
                    style={{
                      marginTop: '1.25rem', padding: '0.75rem 0.85rem', borderRadius: 10,
                      border: '1px solid rgba(251,146,60,0.4)', background: 'rgba(251,146,60,0.07)',
                    }}
                  >
                    <div style={{ fontWeight: 700, color: '#fb923c', fontSize: '0.88rem', marginBottom: '0.4rem' }}>
                      Before this case can ship
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {shipBlockers.map(blocker => (
                        <div key={blocker.key} style={{
                          display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap',
                        }}>
                          <span style={{ color: colors.textBody, fontSize: '0.85rem', lineHeight: 1.55, flex: 1, minWidth: '14rem' }}>
                            {blocker.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p style={{ marginTop: '1.25rem', marginBottom: 0, fontSize: '0.85rem', color: colors.textMuted }}>
                    Everything is settled. Finalizing moves this case to{' '}
                    <strong style={{ color: colors.textBody }}>Awaiting shipping</strong>.
                  </p>
                )}

                <div
                  style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.85rem' }}
                >
                  <button style={modalStyles.buttonAlt} onClick={onClose} disabled={saving}>
                    Close
                  </button>
                  <button
                    style={{
                      ...modalStyles.button,
                      ...(canFinalizeRepair ? {} : { opacity: 0.5, cursor: 'not-allowed' }),
                    }}
                    onClick={handleFinalize}
                    disabled={saving || !canFinalizeRepair}
                    title={canFinalizeRepair ? 'Move this case to Awaiting shipping' : shipBlockers.map(b => b.text).join(' ')}
                  >
                    Finalize Repair
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {feedback && (
          <p style={{ marginTop: '1rem', color: colors.textMuted }}>{feedback}</p>
        )}

        <div style={{ marginTop: '0.75rem', textAlign: 'right' }}>
          <CloseCaseAction rma={rma} onSave={onSave} />
        </div>
      </div>
    </div>
  );
}
